import React from "react";
import { View, Text, Card } from "../base";
import { Icon } from "../base/Icon";
import { VariantRanking, TargetConfig } from "../../utils/chartData";

interface VariantRankingChartProps {
  rankings: VariantRanking[];
  title: string;
  metric: string;
  targetConfig?: TargetConfig | null;
}

export const VariantRankingChart: React.FC<VariantRankingChartProps> = ({ rankings, title, metric, targetConfig }) => {
  if (!rankings || rankings.length === 0) {
    return (
      <Card>
        <View className="p-6">
          <View className="flex items-center mb-4">
            <Icon
              name="trending-up"
              size={20}
              className="mr-2 text-blue-600 dark:text-blue-400"
            />
            <Text className="text-lg font-semibold">{title}</Text>
          </View>
          <View className="flex justify-center items-center h-32">
            <Text className="text-gray-500 dark:text-gray-400">No ranking data available</Text>
          </View>
        </View>
      </Card>
    );
  }

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1:
        return "🥇";
      case 2:
        return "🥈";
      case 3:
        return "🥉";
      default:
        return `${rank}`;
    }
  };

  const getRankColor = (rank: number) => {
    switch (rank) {
      case 1:
        return "text-yellow-600 dark:text-yellow-400";
      case 2:
        return "text-gray-600 dark:text-gray-400";
      case 3:
        return "text-orange-600 dark:text-orange-400";
      default:
        return "text-gray-500 dark:text-gray-500";
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 99) return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
    if (confidence >= 95) return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200";
    if (confidence >= 90) return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200";
    if (confidence >= 80) return "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200";
    return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200";
  };

  const getStatusIcon = (ranking: VariantRanking) => {
    if (ranking.isSignificantlyBetter) return "trending-up";
    if (ranking.isSignificantlyWorse) return "trending-down";
    return "minus";
  };

  const getStatusColor = (ranking: VariantRanking) => {
    if (ranking.isSignificantlyBetter) return "text-green-600 dark:text-green-400";
    if (ranking.isSignificantlyWorse) return "text-red-600 dark:text-red-400";
    return "text-gray-600 dark:text-gray-400";
  };

  const formatValue = (value: number, metric: string) => {
    if (metric === "successRateValue" || metric === "rateValue") {
      return `${value.toFixed(1)}%`;
    }
    return value.toFixed(2);
  };

  return (
    <Card>
      <View className="p-6">
        <View className="flex items-center mb-6">
          <Icon
            name="trending-up"
            size={20}
            className="mr-2 text-blue-600 dark:text-blue-400"
          />
          <Text className="text-lg font-semibold">{title}</Text>
        </View>

        <View className="space-y-4">
          {rankings.map((ranking, index) => (
            <View
              key={ranking.variant}
              className={`p-4 rounded-lg border-2 transition-all ${
                ranking.rank === 1 ? "border-yellow-300 bg-yellow-50 dark:border-yellow-600 dark:bg-yellow-900/20" : "border-gray-200 dark:border-gray-700"
              }`}
            >
              <View className="flex items-center justify-between">
                <View className="flex items-center space-x-4">
                  <View className={`text-2xl font-bold ${getRankColor(ranking.rank)}`}>{getRankIcon(ranking.rank)}</View>

                  <View>
                    <Text className="font-semibold text-lg">{ranking.variant}</Text>
                    <View className="flex items-center space-x-2 mt-1">
                      <Icon
                        name={getStatusIcon(ranking)}
                        size={16}
                        className={getStatusColor(ranking)}
                      />
                      <Text className="text-sm text-gray-600 dark:text-gray-400">{formatValue(ranking.value, metric)}</Text>
                    </View>
                  </View>
                </View>

                <View className="flex items-center space-x-3">
                  <View className={`px-3 py-1 rounded-full text-xs font-medium ${getConfidenceColor(ranking.confidence)}`}>
                    {ranking.confidence >= 95 ? "High Confidence" : ranking.confidence >= 80 ? "Medium Confidence" : "Low Confidence"}
                  </View>

                  <View className="text-right">
                    <Text className="text-sm font-medium">{ranking.significance}</Text>
                    {ranking.comparisons.length > 0 && (
                      <Text className="text-xs text-gray-500 dark:text-gray-400">
                        vs {ranking.comparisons.length} other{ranking.comparisons.length > 1 ? "s" : ""}
                      </Text>
                    )}
                  </View>
                </View>
              </View>

              {/* Show key comparisons */}
              {ranking.comparisons.filter((c) => c.confidence >= 80).length > 0 && (
                <View className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                  <Text className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">Key Comparisons:</Text>
                  <View className="flex flex-wrap gap-2">
                    {ranking.comparisons
                      .filter((c) => c.confidence >= 80)
                      .slice(0, 3)
                      .map((comparison, idx) => (
                        <View
                          key={idx}
                          className={`px-2 py-1 rounded text-xs ${
                            comparison.isBetter
                              ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                              : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                          }`}
                        >
                          <Text className="text-xs">
                            {comparison.isBetter ? "Better" : "Worse"} than {comparison.vsVariant}({comparison.confidence.toFixed(0)}% confidence)
                          </Text>
                        </View>
                      ))}
                  </View>
                </View>
              )}
            </View>
          ))}
        </View>

        {/* Summary */}
        <View className="mt-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <Text className="text-sm font-medium mb-2">Recommendation: </Text>
          {rankings[0]?.isSignificantlyBetter ? (
            <Text className="text-sm text-green-700 dark:text-green-300">
              <strong>{rankings[0].variant}</strong> is significantly better than other variants with {rankings[0].confidence.toFixed(0)}% confidence. Consider
              implementing this variant.
            </Text>
          ) : rankings[0]?.confidence >= 80 ? (
            <Text className="text-sm text-blue-700 dark:text-blue-300">
              <strong>{rankings[0].variant}</strong> appears to be the best performing variant, but the difference may not be statistically significant.
              Consider running the test longer or with more samples.
            </Text>
          ) : (
            <Text className="text-sm text-gray-700 dark:text-gray-300">
              No variant shows statistically significant improvement. Consider running the test longer or with more samples.
            </Text>
          )}

          {targetConfig && (
            <View className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
              <Text className="text-sm font-medium mb-1">Target Configuration: </Text>
              <Text className="text-sm text-gray-600 dark:text-gray-400">
                {targetConfig.type === "maximize"
                  ? "Maximizing values (higher is better)"
                  : targetConfig.type === "minimize"
                    ? "Minimizing values (lower is better)"
                    : `Targeting exact value: ${targetConfig.value}`}
              </Text>
            </View>
          )}
        </View>
      </View>
    </Card>
  );
};
